<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import { afterUpdate, onDestroy, onMount } from "svelte";
import { fly } from "svelte/transition";
import Alert from "../../components/Alert.svelte";
import FeedbackButtons from "../../components/FeedbackButtons.svelte";
import InputTextarea from "../../components/InputTextarea.svelte";
import LoaderAnimation from "../../components/LoaderAnimation.svelte";
import ModelSelection from "../../components/ModelSelection.svelte";
import sendFeedback from "../../utils/SendFeedback.js";
import uiStore from "../../utils/UIStore";
import { renderMarkdown } from "../../utils/markdownParser";
import websocketsStore from "../../utils/websocketsStore";

import ExamplePrompts from "../../components/ExamplePrompts.svelte";

// component state variables
let messages = [];
let newMessage = "";
let lastUserMessage = "";
let llm = {};
let llm_selected_by_user = false;
let available_llms = [];
let sendButtonTooltip = "";
let chatText = "";
let warningCloud = "";
let warningChat = "";
let resetTextArea;
let examplePrompts = [];
let currentReasonText = "";
let showReasoning = false;

const streamingStatus = {
	Ready: "ready",
	Loading: "loading",
	Streaming: "streaming",
	Error: "error",
};
let currentStreamingStatus = streamingStatus.Ready;

//---Svelte Component Lifecycle
onMount(() => {
	updateSendButtonTooltip();
});

//load current state from uistore
uiStore.chatStore.subscribe((store) => {
	llm = store.llm;
	llm_selected_by_user = store.llm_selected_by_user;
	newMessage = store.newMessage;
	lastUserMessage = store.lastUserMessage;

	messages = store.messages;
	if (messages.length > 0 && messages[messages.length - 1].role === "user") {
		rollBackOnError();
	}
});

//load available llms from uistore and if available set local llm to current llm
uiStore.active_llms_chat.subscribe((active_llms) => {
	available_llms = active_llms.chat;
	const local_model = active_llms.chat.find((item) => item.name === "local");
	if (!llm_selected_by_user) {
		if (local_model) {
			llm = local_model;
		} else {
			llm = available_llms[0];
		}
	}
});

//load ui texts from ui Store
uiStore.textStore.subscribe((store) => {
	if (store.texts != null) {
		const data = store.texts;
		chatText = renderMarkdown(data.chatText);
		warningCloud = data.warnhinweisCloud;
		warningChat = renderMarkdown(data.warnhinweisChat);
	}
});

let uiTexts;
uiStore.textStore.subscribe((store) => {
	uiTexts = store.texts;
});

$: examplePrompts = uiTexts ? uiTexts.examplePromptsChat : [];

//update uistore before end of component
onDestroy(() => {
	uiStore.chatStore.update(() => ({
		messages,
		newMessage,
		lastUserMessage,
		llm,
		llm_selected_by_user,
	}));
});

let lastUpdatedMsg = null;
// Auto-Scroll on new messages
const scrollToLastChatItem = async (node) => {
	let lastMsg = null;

	//if answer is currently loading -> scroll to loading animation
	if (currentStreamingStatus === streamingStatus.Loading) {
		lastMsg = "loading-icon";
	} else if (currentStreamingStatus === streamingStatus.Error) {
		//if answer is error-> scroll to error message
		lastMsg = "error-icon";
	} else if (messages.length > 1) {
		//else get last message from alle messages
		lastMsg = getMessageIdx(messages.length - 1);
	}
	if (lastMsg !== lastUpdatedMsg && lastMsg != null) {
		//only update scrollposition if there is a new last Message
		lastUpdatedMsg = lastMsg;
		const lastMsgElement = document.getElementById(lastMsg);
		if (lastMsgElement) {
			const topPos = lastMsgElement.offsetTop;
			node.scroll({ top: topPos, behavior: "smooth" });
		}
	}
};

let chatWindow;
afterUpdate(() => {
	if (messages) scrollToLastChatItem(chatWindow);
});

function handleEnterKey(event) {
	// send message on [Enter]
	if (event.key === "Enter" && !event.shiftKey) {
		sendMessage();
		event.preventDefault();
	}

	// newline on [SHIFT+Enter]
	if (event.key === "Enter" && event.shiftKey) {
		newMessage = `${newMessage}\n`;
		event.preventDefault();
	}
}

async function sendMessage() {
	websocketsStore.websocket.resetMessages();
	if (newMessage.length > 0) {
		const _newMessageObj = {
			content: newMessage,
			role: "user",
		};

		const chat_history = [...messages];
		messages = [...messages, _newMessageObj];
		lastUserMessage = newMessage;
		newMessage = "";
		resetTextAreaHeight();
		currentReasonText = "";
		showReasoning = false;

		currentStreamingStatus = streamingStatus.Loading;

		try {
			const response = await fetch(
				`${process.env.API_URL || ""}/chat/v2/completion/stream`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						new_message: _newMessageObj,
						chat_history,
						language_model: llm.name,
						request_timestamp: Date.now(),
					}),
				},
			);

			if (response.status === 200) {
				let responseText = "";
				let reasonText = "";
				messages = [
					...messages,
					{
						role: "assistant",
						content: responseText,
						rating: 0,
					},
				];

				currentStreamingStatus = streamingStatus.Streaming;

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = "";

				while (true) {
					const { done, value } = await reader.read();
					if (done) {
						if (messages[messages.length - 1].content === "") {
							websocketsStore.currentMessage.error.update(() => ({
								type: "ERROR",
								message: uiTexts.chat.noAnswerGenerated,
							}));
						}
						currentStreamingStatus = streamingStatus.Ready;
						break;
					}

					buffer += decoder.decode(value, { stream: true });
					let lines = buffer.split("\n");
					buffer = lines.pop(); // last line may be incomplete
					for (const line of lines) {
						if (!line.trim()) continue;
						let data;
						try {
							data = JSON.parse(line);
						} catch (e) {
							continue;
						}
						if (data.type === "error") {
							websocketsStore.currentMessage.error.update(() => ({
								type: "ERROR",
								message: data.error,
							}));
							currentStreamingStatus = streamingStatus.Error;
							rollBackOnError();
							return;
						}
						if (data.type === "reason") {
							reasonText += data.content;
							currentReasonText = reasonText;
						} else if (data.type === "response") {
							if (data.finish_reason === "stop") {
								currentStreamingStatus = streamingStatus.Ready;
								break;
							}
							responseText += data.content;
							messages[messages.length - 1].content = responseText;
						}
					}
				}
			} else {
				const res = await response.json();
				throw new Error(res.detail || "Fehler beim Abrufen der Antwort");
			}
		} catch (err) {
			websocketsStore.currentMessage.error.update(() => ({
				type: "ERROR",
				message: err.message,
			}));

			currentStreamingStatus = streamingStatus.Error;

			rollBackOnError();
		}
	}
}

function rollBackOnError() {
	currentStreamingStatus = streamingStatus.Ready;
	if (messages.length === 0) {
		return;
	}

	if (messages[messages.length - 1].role === "user") {
		messages = messages.slice(0, messages.length - 1);
		newMessage = lastUserMessage;
	}
}

function resetUI() {
	messages = [];
	newMessage = "";
	lastUserMessage = "";
	resetTextAreaHeight();
	websocketsStore.websocket.resetMessages();
}

async function giveFeedback(selected_message, rating, feedback_text) {
	selected_message.rating = rating;

	const body = {
		feedback: rating,
		feedback_text: feedback_text,
		chat_answer: selected_message.content,
		user_prompt: messages[messages.length - 2].content,
		llm: {
			name: llm.name,
			label: llm.label,
			is_remote: llm.is_remote,
		},
	};

	sendFeedback(body, `${process.env.API_URL}/feedback/chat`);
}

function getMessageIdx(idx) {
	return `message_${idx}`;
}

$: if (newMessage != null) {
	updateSendButtonTooltip();
}

$: if (
	currentStreamingStatus === streamingStatus.Streaming ||
	currentStreamingStatus === streamingStatus.Ready
) {
	updateSendButtonTooltip();
}

function updateSendButtonTooltip() {
	if (currentStreamingStatus === streamingStatus.Streaming) {
		sendButtonTooltip = uiTexts.chat.tooltipWait;
	} else if (newMessage === "") {
		sendButtonTooltip = uiTexts.chat.tooltipEnterRequest;
	} else {
		sendButtonTooltip = uiTexts.chat.tooltipSend;
	}
}

function resetTextAreaHeight() {
	resetTextArea();
}

function useExamplePrompt(prompt) {
	newMessage = prompt.samplePrompt;
}
</script>

<svelte:head>
  <title>{uiTexts.pageTitles.chat}</title>
</svelte:head>

<main
  style="padding-top: 90px;display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: center;"
  in:fly={{ y: -20, duration: 500, delay: 200 }}
>
  {#if !llm || llm.is_remote}
    <div class="top-info-bar">
      <Alert>
        <span class="red">{uiTexts.warning.attention}</span>
        <span style="color:#000">{warningCloud} </span>
      </Alert>
    </div>
  {/if}
  <div class="page-container">
    <div class="hinweis">
      <div style="flex:1;padding-right:24px" class="textlink">
        {@html chatText}
      </div>
      <div class="reset">
        <button
          class="secondary delete"
          on:click={resetUI}
          disabled={currentStreamingStatus === streamingStatus.Streaming}
          >{uiTexts.chat.resetButton}
        </button>
      </div>
    </div>

    <div class="content-container" id="scrollTo">
      <div
        class="chat-history"
        bind:this={chatWindow}
        style="margin-bottom:12px;"
      >
        {#each messages as message, idx}
          <div class="chat-item" id={getMessageIdx(idx)}>
            {#if message.role === "assistant" && idx === messages.length - 1 && currentReasonText}
              <div class="chat-icon-assistant"></div>
              <div class="chat-content" style="display: flex; flex-direction: column; gap: 0.5rem;">
                <div>
                  <div
                    class="chat-role"
                    style="display: flex; align-items: center; cursor: pointer;"
                    on:click={() => showReasoning = !showReasoning}
                    on:keydown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        showReasoning = !showReasoning;
                        e.preventDefault();
                      }
                    }}
                    tabindex="0"
                    role="button"
                    aria-pressed={showReasoning}
                  >
                    Denk-Prozess
                    <span style="margin-left: 6px; font-size: 1.1em; user-select: none;">
                      {showReasoning ? '▴' : '▾'}
                    </span>
                  </div>
                  {#if showReasoning}
                    <div class="chat-text reasoning-pipe" style="color: #666; font-size: 0.95em;">{currentReasonText}</div>
                  {/if}
                </div>
                <div>
                  <div class="chat-role">{uiTexts.chat.roleAssistant}</div>
                  <div class="chat-text">{@html renderMarkdown(message.content)}</div>
                  {#if currentStreamingStatus != streamingStatus.Streaming}
                    <FeedbackButtons
                      messageContent={message.content}
                      buttonClicked={message.rating}
                      callback={(rating, feedback_text) =>
                        giveFeedback(message, rating, feedback_text)}
                    />
                  {/if}
                </div>
              </div>
            {:else}
              {#if message.role === "assistant"}
                <div class="chat-icon-assistant"></div>
              {/if}
              {#if message.role === "user"}
                <div class="chat-icon-user"></div>
              {/if}
              <div class="chat-content">
                <div class="chat-role">
                  {#if message.role === "assistant"}
                    {uiTexts.chat.roleAssistant}
                  {/if}
                  {#if message.role === "user"}
                    {uiTexts.chat.roleUser}
                  {/if}
                </div>
                <div class="chat-text">
                  {@html renderMarkdown(message.content)}
                </div>
                {#if message.role === "assistant" && currentStreamingStatus != streamingStatus.Streaming}
                  <FeedbackButtons
                    messageContent={message.content}
                    buttonClicked={message.rating}
                    callback={(rating, feedback_text) =>
                      giveFeedback(message, rating, feedback_text)}
                  />
                {/if}
              </div>
            {/if}
          </div>
        {/each}

        {#if currentStreamingStatus === streamingStatus.Error}
          <div class="chat-item" id="error-icon">
            <div class="chat-icon-assistant"></div>
            <div class="chat-content">
              <div class="chat-role">{uiTexts.chat.systemWarning}</div>
              <div class="chat-text">
                {uiTexts.chat.errorMessage}
              </div>
            </div>
          </div>
        {/if}

        {#if currentStreamingStatus === streamingStatus.Loading}
          <div class="chat-item" id="loading-icon">
            <div class="chat-icon-assistant"></div>
            <div class="chat-content">
              <div class="chat-role">{uiTexts.chat.roleAssistant}</div>
              <div class="chat-text" style="margin-top:1rem;">
                <LoaderAnimation />
              </div>
            </div>
          </div>
        {/if}
      </div>

      <InputTextarea
        bind:newMessage
        {handleEnterKey}
        bind:reset={resetTextArea}
        name="chat-eingabe"
        placeholder={uiTexts.chat.inputPlaceholder}
      ></InputTextarea>

      <div class="input-actions">
        <div class="halluzination-warning">
          {@html warningChat}
        </div>

        <div
          style="display:flex;flex-direction:row; align-items:end; gap:20px;"
        >
          <ModelSelection
            bind:relevantStore={uiStore.active_llms_chat}
            bind:availableLLMs={available_llms}
            bind:llmIsSelected={llm_selected_by_user}
            bind:selectedLLM={llm}
            pageName="Chat"
          />
          <button
            class="send-button"
            aria-label={uiTexts.chat.sendAriaLabel}
            disabled={newMessage.length === 0 ||
              currentStreamingStatus === streamingStatus.Streaming}
            on:click={() => sendMessage()}
            title={sendButtonTooltip}
          >
          </button>
        </div>
      </div>
    </div>
    <!-- /Example Prompts -->
    {#if examplePrompts.length > 0}
      <ExamplePrompts
        prompts={examplePrompts}
        translationKey="examplePromptsChat"
        callback={(prompt) => useExamplePrompt(prompt)}
      />
    {/if}
  </div>
</main>

<style>
  * {
    box-sizing: border-box;
  }
  .hinweis {
    width: 100%;
    max-width: 1200px;
    display: flex;
    text-align: left;
    margin-bottom: 12px;
  }
  .content-container {
    width: 100%;
    padding: 12px;
    max-width: 1200px;
    margin: 0 auto 12px;
    display: flex;
    flex-direction: column;
    background-color: var(--bg-color);
  }

  .chat-history {
    width: 100%;
    min-height: 200px;
    max-height: calc(100vh - 600px);
    text-align: left;
    padding: 10px;
    overflow-y: auto;
    background-color: #fff;
    position: relative;
  }

  .chat-history > div {
    display: flex;
  }

  .chat-history > div {
    display: flex;
    margin-bottom: 30px;
    align-items: flex-start;
  }

  .chat-history > div .chat-icon-user,
  .chat-history > div .chat-icon-assistant {
    width: 32px;
    height: 32px;
    border-radius: 16px;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 90%;
    margin-right: 20px;
  }

  .chat-history > div .chat-icon-assistant {
    background-image: url("static/icon-assistant.svg");
  }

  .chat-history > div .chat-icon-user {
    background-image: url("static/icon-user.svg");
  }

  .chat-history > div .chat-role {
    font-weight: bold;
  }

  .chat-history > div .chat-content {
    width: calc(100% - 60px);
  }

  .reasoning-pipe {
    border-left: 2px solid #bbb;
    padding-left: 12px;
    margin-left: 2px;
  }

  @media screen and (max-width: 1200px) {
    .hinweis {
      padding: 0 12px;
    }
  }
</style>
